import { Button, Card, Checkbox, Col, Form, message, Row } from "antd";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import cmaApi from "../../../../../api/cmaApi";
import InputField from "../../../../../components/InputField";
import InputNumberWrap from "../../../../../components/InputNumber";
import Loading from "../../../../../components/Loading";
import TextAreaField from "../../../../../components/TextAreaField";
import TopContent from "../../../../../components/TopContent";
import PageWrap from "../../../../../layout/PageWrap";
import { appRoutes } from "../../../../../routes/routes";
import { formItemLayoutSm } from "../../../../../utils/constants";
import { messages } from "../../../../../utils/messages";

interface TestimonialsFormProps {
  type: number;
}

const TestimonialsForm: FC<TestimonialsFormProps> = ({ type }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const { data, isLoading } = useQuery(
    ["cma_landing_testimonials", id],
    () => cmaApi.getLandingTestimonialDetails(id),
    { enabled: type === 2 }
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const mutationEdit = useMutation(cmaApi.editLandingTestimonial, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.updateSuccess);
        await queryClient.invalidateQueries("cma_landing_testimonials");
        navigate(appRoutes.CMA.LANDING.TESTIMONIALS.LIST);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const mutationAdd = useMutation(cmaApi.addLandingTestimonial, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.addSuccess);
        await queryClient.invalidateQueries("cma_landing_testimonials");
        navigate(appRoutes.CMA.LANDING.TESTIMONIALS.LIST);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const onSubmit = (dt: any) => {
    const payload = {
      ...dt,
      rating: Number(dt.rating),
      hienthi:
        dt?.hienthi ??
        (type === 2 ? data?.data?.hienthi ?? false : false),
    };
    if (type === 2) mutationEdit.mutate({ id, data: payload });
    if (type === 1) mutationAdd.mutate(payload);
  };

  return (
    <PageWrap
      title="CMA · Đánh giá"
      content={
        <>
          <TopContent
            content={
              <Row>
                <Col>
                  <b>CMA · Đánh giá</b>
                </Col>
              </Row>
            }
          />
          <div className="content">
            {isLoading ? (
              <Loading />
            ) : (
              <Card bordered={false}>
                <Form {...formItemLayoutSm} className="slider-form">
                  <InputField
                    label="Initials"
                    name="initials"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.initials}
                    errorMsg={errors?.initials?.message}
                    rules={{
                      required: "Vui lòng nhập initials",
                      maxLength: {
                        value: 3,
                        message: "Tối đa 3 ký tự",
                      },
                    }}
                  />
                  <InputNumberWrap
                    label="Rating (1-5)"
                    name="rating"
                    control={control}
                    defaultValue={data?.data?.rating ?? 5}
                    min={1}
                    max={5}
                    errorMsg={errors?.rating?.message}
                    rules={{ required: "Vui lòng nhập rating" }}
                  />
                  <TextAreaField
                    label="Quote (VI)"
                    name="quote_vi"
                    control={control}
                    defaultValue={data?.data?.quote_vi}
                    errorMsg={errors?.quote_vi?.message}
                    rules={{ required: "Vui lòng nhập quote" }}
                  />
                  <TextAreaField
                    label="Quote (EN)"
                    name="quote_en"
                    control={control}
                    defaultValue={data?.data?.quote_en}
                  />
                  <InputField
                    label="Name (VI)"
                    name="name_vi"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.name_vi}
                    errorMsg={errors?.name_vi?.message}
                    rules={{ required: "Vui lòng nhập tên" }}
                  />
                  <InputField
                    label="Name (EN)"
                    name="name_en"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.name_en}
                  />
                  <InputField
                    label="Role (VI)"
                    name="role_vi"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.role_vi}
                    errorMsg={errors?.role_vi?.message}
                    rules={{ required: "Vui lòng nhập role" }}
                  />
                  <InputField
                    label="Role (EN)"
                    name="role_en"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.role_en}
                  />
                  <InputNumberWrap
                    label="STT"
                    name="stt"
                    control={control}
                    defaultValue={data?.data?.stt ?? 1}
                    onBlur={(e: any) => {
                      if (e.target.value === "") setValue("stt", 1);
                    }}
                    min={1}
                  />
                  <Form.Item className="checkbox">
                    <Row>
                      <Col xs={24} sm={20}>
                        <Checkbox
                          defaultChecked={data?.data?.hienthi}
                          name="hienthi"
                          onClick={(e: any) => {
                            setValue("hienthi", e.target.checked);
                          }}
                        />
                        &nbsp;Hiển thị
                      </Col>
                    </Row>
                  </Form.Item>
                  <Row justify="start" gutter={[10, 0]}>
                    <Col>
                      <Button
                        type="primary"
                        danger
                        onClick={handleSubmit(onSubmit)}
                      >
                        Lưu
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() =>
                          navigate(appRoutes.CMA.LANDING.TESTIMONIALS.LIST)
                        }
                      >
                        Hủy
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card>
            )}
          </div>
        </>
      }
    />
  );
};

export default TestimonialsForm;
